import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Loader,
  InputField,
  Label,
} from '@energybox/react-ui-library/dist/components';
import {
  EUIUnit,
  EUIUnitLabels,
} from '@energybox/react-ui-library/dist/types/Global';
import { FixedContentWrapper } from '../../components/Page';
import { PageContentHeader } from '../../components/Page';
import { useCurrentUser } from '../../hooks/useAppDetails';
import styles from './ShowOrgLevelEfficiencySopsPage.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../reducers';
import { Sop, SopTypes } from '@energybox/react-ui-library/dist/types';
import { useCurrentOrganizationId } from '../../hooks/useCurrentOrganization';
import { Efficiency } from '@energybox/react-ui-library/dist/types/Sop';
import {
  update as updateSop,
  create as createSop,
  updateField,
  reset as resetSop,
} from '../../actions/sops';
import { global } from '@energybox/react-ui-library/dist/utils';
import {
  convertEuiFromKwhM2,
  toKwhM2,
} from '@energybox/react-ui-library/dist/utils/eui';

interface State {
  euiTarget: number | null;
  isChanged: boolean;
}

const ShowOrgLevelEfficiencySopsPage: React.FC = () => {
  const dispatch = useDispatch();
  const orgId = useCurrentOrganizationId();
  const currentUser = useCurrentUser();
  const userEuiUnit = currentUser?.energyUseIntensityUnit;
  const euiUnit = userEuiUnit || EUIUnit.KWH_M2;
  const [initialized, setInitialized] = useState(false);

  const [state, setState] = useState<State>({
    euiTarget: null,
    isChanged: false,
  });

  const efficiencySop = useSelector<ApplicationState, Sop | undefined>(
    ({ sops }) => {
      return Object.values(sops.sopsById).find(item => {
        return (
          item.components[0]?.type === SopTypes.EUI_EFFICIENCY &&
          item.organizationUnitId === orgId
        );
      });
    }
  );
  const isSopDataLoading = useSelector<ApplicationState, boolean>(
    ({ sops }) => sops.isSopDataLoading
  );

  const editSopId = efficiencySop ? String(efficiencySop.id) : 'new';
  const editSop = useSelector<ApplicationState, any>(
    ({ sops }) => sops.editById[editSopId]
  );

  const initialEuiTarget = (efficiencySop?.components[0] as Efficiency)
    ?.euiTarget;

  useEffect(() => {
    if (isSopDataLoading) return;
    setState(prev => ({
      ...prev,
      euiTarget: Math.round(
        convertEuiFromKwhM2(initialEuiTarget, euiUnit) || 500
      ),
    }));
  }, [initialEuiTarget, isSopDataLoading]);

  // Initialize editById for existing or new SOPs
  useEffect(() => {
    if (initialized || !orgId) return;

    // Existing SOP: ensure editById is populated from store
    if (efficiencySop && !editSop) {
      dispatch(resetSop(String(efficiencySop.id)));
      setInitialized(true);
      return;
    }

    // New SOP: seed editById with defaults
    if (!efficiencySop && !editSop) {
      dispatch(updateField('new', 'title', 'EUI Target'));
      dispatch(updateField('new', 'description', ''));
      dispatch(
        updateField('new', 'components', [
          {
            type: SopTypes.EUI_EFFICIENCY,
            euiTarget: initialEuiTarget || 500,
          },
        ])
      );
      dispatch(updateField('new', 'organizationUnitId', orgId));
      setInitialized(true);
    }
  }, [
    dispatch,
    efficiencySop,
    editSop,
    orgId,
    initialized,
    initialEuiTarget,
    isSopDataLoading,
  ]);

  // Update state when user preference becomes available (if not manually changed)
  // only happens when previous user preference was null and is now available
  useEffect(() => {
    if (!state.isChanged && userEuiUnit) {
      setState(prev => ({
        ...prev,
        euiTarget: Math.round(
          convertEuiFromKwhM2(initialEuiTarget, euiUnit) || 500
        ),
      }));
    }
  }, [userEuiUnit]);

  const handleEuiTargetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    const numValue = value === '' ? 0 : parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      dispatch(
        updateField(editSopId, 'components.0.type', SopTypes.EUI_EFFICIENCY)
      );
      dispatch(updateField(editSopId, 'components.0.euiTarget', numValue));

      setState(prev => ({
        ...prev,
        euiTarget: numValue,
        isChanged: true,
      }));
    }
  };

  const handleSave = () => {
    let convertedEuiTarget = state.euiTarget;
    if (euiUnit !== EUIUnit.KWH_M2) {
      convertedEuiTarget = Math.round(toKwhM2(state.euiTarget, euiUnit) || 500);
    }

    dispatch(updateField(editSopId, 'title', 'EUI Target'));
    dispatch(updateField(editSopId, 'organizationUnitId', orgId));
    dispatch(updateField(editSopId, 'description', ''));
    dispatch(
      updateField(editSopId, 'components.0.type', SopTypes.EUI_EFFICIENCY)
    );
    dispatch(
      updateField(editSopId, 'components.0.euiTarget', convertedEuiTarget)
    );

    if (efficiencySop && editSopId !== 'new') {
      dispatch(updateSop(efficiencySop.id, 'efficiency', { orgId }));
    } else {
      dispatch(createSop('efficiency', orgId));
    }
    setState(prev => ({
      ...prev,
      isChanged: false,
    }));
  };

  const handleCancel = () => {
    if (efficiencySop) {
      dispatch(resetSop(String(efficiencySop.id)));
    } else {
      dispatch(
        updateField('new', 'components.0.type', SopTypes.EUI_EFFICIENCY)
      );
      dispatch(
        updateField('new', 'components.0.euiTarget', initialEuiTarget || 500)
      );
      dispatch(updateField('new', 'organizationUnitId', orgId));
    }

    setState(prev => ({
      ...prev,
      euiTarget: Math.round(
        convertEuiFromKwhM2(initialEuiTarget, userEuiUnit || EUIUnit.KWH_M2) ||
          500
      ),
      euiUnit: userEuiUnit || EUIUnit.KWH_M2,
      isChanged: false,
    }));
  };

  return (
    <FixedContentWrapper>
      <PageContentHeader header='Efficiency' />
      <Card>
        <CardContent>
          <form className={styles.form}>
            <div>
              <h4>Energy Use Intensity (EUI)</h4>
              <div className={styles.inputGroup}>
                <Label htmlFor='eui-target'>EUI Yearly Target</Label>
                <div className={styles.euiInputWrapper}>
                  <InputField
                    id='eui-target'
                    name='euiTarget'
                    type='number'
                    value={
                      isSopDataLoading
                        ? (global.NOT_AVAILABLE as string)
                        : state.euiTarget?.toString() || ''
                    }
                    onChange={handleEuiTargetChange}
                    placeholder={isSopDataLoading ? global.NOT_AVAILABLE : ''}
                    className={styles.euiInput}
                    disabled={isSopDataLoading}
                  />
                  <Label htmlFor='eui-unit'>{EUIUnitLabels[euiUnit]}</Label>
                </div>
              </div>
            </div>
          </form>
        </CardContent>

        {state.isChanged && (
          <CardActions>
            <Button variant='text' onClick={handleCancel}>
              Cancel
            </Button>
            <Button disabled={isSopDataLoading} onClick={handleSave}>
              {isSopDataLoading ? (
                <Loader size={16} variant='secondary' />
              ) : (
                'Save changes'
              )}
            </Button>
          </CardActions>
        )}
      </Card>
    </FixedContentWrapper>
  );
};

export default ShowOrgLevelEfficiencySopsPage;
